import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useCallback, useRef, useState } from "react";
import "./MoleculeEditor.css";

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const downloadString = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  downloadBlob(blob, filename);
};

const MoleculeEditor = () => {
  const iframeRef = useRef(null);
  const toast = useRef(null);
  const [smilesInput, setSmilesInput] = useState("");
  const [ketcherReady, setKetcherReady] = useState(false);
  const [loading, setLoading] = useState(false);

  // ====== Ketcher API 助手 ======
  const getKetcherApi = useCallback(() => {
    try {
      return iframeRef.current?.contentWindow?.ketcher || null;
    } catch {
      return null;
    }
  }, []);

  const waitForKetcherApi = useCallback(async (timeout = 10000) => {
    const deadline = Date.now() + timeout;
    while (Date.now() < deadline) {
      const api = getKetcherApi();
      if (api && typeof api.setMolecule === "function") return api;
      await new Promise((r) => setTimeout(r, 300));
    }
    return null;
  }, [getKetcherApi]);

  // iframe 加载后等待 Ketcher 就绪
  const handleIframeLoad = useCallback(() => {
    // 延迟检查，给 Ketcher 初始化时间
    setTimeout(async () => {
      const api = await waitForKetcherApi(15000);
      setKetcherReady(!!api);
    }, 500);
  }, [waitForKetcherApi]);

  // ====== 操作处理 ======

  // 加载 SMILES 到 Ketcher
  const handleLoadSmiles = useCallback(async () => {
    const smiles = smilesInput.trim();
    if (!smiles) {
      toast.current?.show({ severity: "warn", summary: "提示", detail: "请输入 SMILES" });
      return;
    }
    setLoading(true);
    const api = await waitForKetcherApi();
    if (!api) {
      toast.current?.show({ severity: "error", summary: "错误", detail: "Ketcher 未就绪，请稍后重试" });
      setLoading(false);
      return;
    }
    try {
      await api.setMolecule(smiles);
      if (api.editor) {
        api.editor.setOptions(JSON.stringify({ showHydrogenLabels: "off" }));
      }
      toast.current?.show({ severity: "success", summary: "已加载", detail: "SMILES 已加载到编辑器" });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "加载失败", detail: String(e) });
    }
    setLoading(false);
  }, [smilesInput, waitForKetcherApi]);

  // 回车加载
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") handleLoadSmiles();
  }, [handleLoadSmiles]);

  // 清空编辑器
  const handleClear = useCallback(async () => {
    setSmilesInput("");
    const api = await waitForKetcherApi(5000);
    if (api) {
      try { await api.setMolecule(""); } catch {}
    }
    toast.current?.show({ severity: "info", summary: "已清空", detail: "编辑器已清空" });
  }, [waitForKetcherApi]);

  // 复制 SMILES
  const handleCopySmiles = useCallback(async () => {
    const api = await waitForKetcherApi();
    if (!api) {
      toast.current?.show({ severity: "error", summary: "错误", detail: "Ketcher 未就绪，请稍后重试" });
      return;
    }
    try {
      const smiles = await api.getSmiles();
      if (!smiles) {
        toast.current?.show({ severity: "warn", summary: "无数据", detail: "编辑器中没有分子" });
        return;
      }
      await navigator.clipboard.writeText(smiles);
      const preview = smiles.length > 60 ? smiles.substring(0, 60) + "..." : smiles;
      toast.current?.show({ severity: "success", summary: "已复制", detail: `SMILES: ${preview}` });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "复制失败", detail: String(e) });
    }
  }, [waitForKetcherApi]);

  // 复制 SMARTS
  const handleCopySmarts = useCallback(async () => {
    const api = await waitForKetcherApi();
    if (!api) {
      toast.current?.show({ severity: "error", summary: "错误", detail: "Ketcher 未就绪，请稍后重试" });
      return;
    }
    try {
      const smarts = await api.getSmarts();
      if (!smarts) {
        toast.current?.show({ severity: "warn", summary: "无数据", detail: "编辑器中没有分子" });
        return;
      }
      await navigator.clipboard.writeText(smarts);
      const preview = smarts.length > 60 ? smarts.substring(0, 60) + "..." : smarts;
      toast.current?.show({ severity: "success", summary: "已复制", detail: `SMARTS: ${preview}` });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "复制失败", detail: String(e) });
    }
  }, [waitForKetcherApi]);

  // 导出 PNG
  const handleExportPng = useCallback(async () => {
    const api = await waitForKetcherApi();
    if (!api) {
      toast.current?.show({ severity: "error", summary: "错误", detail: "Ketcher 未就绪，请稍后重试" });
      return;
    }
    try {
      const molfile = await api.getMolfile();
      if (!molfile) {
        toast.current?.show({ severity: "warn", summary: "无数据", detail: "编辑器中没有分子" });
        return;
      }
      const blob = await api.generateImage(molfile, { outputFormat: "png" });
      if (!blob || blob.size === 0) throw new Error("生成的图片为空");
      downloadBlob(blob, "molecule.png");
      toast.current?.show({ severity: "success", summary: "已导出", detail: "PNG 已下载" });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "导出 PNG 失败", detail: String(e) });
    }
  }, [waitForKetcherApi]);

  // 导出 SVG
  const handleExportSvg = useCallback(async () => {
    const api = await waitForKetcherApi();
    if (!api) {
      toast.current?.show({ severity: "error", summary: "错误", detail: "Ketcher 未就绪，请稍后重试" });
      return;
    }
    try {
      const molfile = await api.getMolfile();
      if (!molfile) {
        toast.current?.show({ severity: "warn", summary: "无数据", detail: "编辑器中没有分子" });
        return;
      }
      const result = await api.generateImage(molfile, { outputFormat: "svg" });
      if (typeof result === "string") {
        downloadString(result, "molecule.svg", "image/svg+xml");
      } else {
        const blob = result instanceof Blob ? result : new Blob([result], { type: "image/svg+xml" });
        downloadBlob(blob, "molecule.svg");
      }
      toast.current?.show({ severity: "success", summary: "已导出", detail: "SVG 已下载" });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "导出 SVG 失败", detail: String(e) });
    }
  }, [waitForKetcherApi]);

  // 下载 MOL 文件
  const handleDownloadMol = useCallback(async () => {
    const api = await waitForKetcherApi();
    if (!api) {
      toast.current?.show({ severity: "error", summary: "错误", detail: "Ketcher 未就绪，请稍后重试" });
      return;
    }
    try {
      const molfile = await api.getMolfile();
      if (!molfile) {
        toast.current?.show({ severity: "warn", summary: "无数据", detail: "编辑器中没有分子" });
        return;
      }
      downloadString(molfile, "molecule.mol", "chemical/x-mdl-molfile");
      toast.current?.show({ severity: "success", summary: "已下载", detail: "MOL 文件已下载" });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "下载 MOL 失败", detail: String(e) });
    }
  }, [waitForKetcherApi]);

  return (
    <div className="molecule-editor">
      <Toast ref={toast} position="bottom-right" />

      <div className="editor-header">
        <i className="pi pi-pencil editor-header-icon" />
        <h2 className="editor-title">分子编辑器</h2>
      </div>

      <div className="editor-main">
        <div className="editor-left-column">
          <div className="editor-input-row">
            <span className="editor-smiles-label">SMILES：</span>
            <div className="editor-input-wrapper">
              <InputText
                value={smilesInput}
                onChange={(e) => setSmilesInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder='输入 SMILES，例如：CCO'
                className="editor-smiles-input"
              />
            </div>
            <Button
              label="加载"
              icon="pi pi-upload"
              onClick={handleLoadSmiles}
              loading={loading}
              className="p-button-sm editor-btn"
            />
            <Button
              label="清空"
              icon="pi pi-times"
              onClick={handleClear}
              className="p-button-sm p-button-outlined editor-btn"
            />
          </div>

          <div className="editor-ketcher-panel">
          <iframe
            ref={iframeRef}
            src="/ketcher/index.html?v=2"
            title="Ketcher Structure Editor"
            className="ketcher-iframe"
            onLoad={handleIframeLoad}
          />
          {!ketcherReady && (
            <div className="ketcher-loading-overlay">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: 32, color: "#6366f1" }} />
              <p className="ketcher-loading-text">加载 Ketcher 编辑器中...</p>
            </div>
          )}
        </div>
      </div>

      <div className="editor-actions-panel">
          <h3 className="actions-title">操作</h3>
          <div className="actions-list">
            <Button
              label="复制 SMILES"
              icon="pi pi-copy"
              className="p-button-outlined p-button-sm action-btn"
              onClick={handleCopySmiles}
            />
            <Button
              label="复制 SMARTS"
              icon="pi pi-copy"
              className="p-button-outlined p-button-sm action-btn"
              onClick={handleCopySmarts}
            />
            <Button
              label="导出 PNG"
              icon="pi pi-image"
              className="p-button-outlined p-button-sm action-btn"
              onClick={handleExportPng}
            />
            <Button
              label="导出 SVG"
              icon="pi pi-file"
              className="p-button-outlined p-button-sm action-btn"
              onClick={handleExportSvg}
            />
            <Button
              label="下载 MOL"
              icon="pi pi-download"
              className="p-button-outlined p-button-sm action-btn"
              onClick={handleDownloadMol}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoleculeEditor;
